/**
 * Shell Application - Persistent MFE Loader
 * Project: Quantiix Shell (quantiix-shell)
 * Module: src/components/PersistentMFELoader.jsx
 *
 * Generic loader for all non-routed, persistent MFEs (panels, overlays).
 * Driven entirely by MicroFrontend.js config — no new Shell files needed
 * when adding future persistent MFEs.
 *
 * Replaces dedicated panel components (ReportsPanel, ShopFloorAIPanel, etc.)
 *
 * Config shape:
 *   panelMode: 'bottom' | 'overlay'
 *   visibleWith: ['dashboard', 'publicview', 'machine']
 *
 * Usage in Layout:
 *   <PersistentMFELoader.Bottom />  — inside flex column (document flow)
 *   <PersistentMFELoader.Overlay /> — outside flex column (fixed position)
 */

import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getPersistentMicroFrontends } from '@/config/MicroFrontend';
import MicroFrontendLoader from '@/micro-frontends/MicroFrontendLoader';
import { createLogger } from '@/utils/logger';

const logger = createLogger('Shell::PersistentMFELoader');

const PANEL_STYLES = {
    bottom: {
        padding: '8px var(--ds-spacing-xl, 24px) var(--ds-spacing-xl, 24px) var(--ds-spacing-xl, 24px)',
        background: 'transparent',
    },
};

/**
 * Shared hook — resolves which persistent MFEs should be visible.
 */
const usePersistentMFEs = () => {
    const location = useLocation();
    const { hasFeature } = useAuth();

    const persistentMFEs = useMemo(() => getPersistentMicroFrontends(), []);

    const activeAppId = useMemo(() => {
        if (!location.pathname.startsWith('/app/')) return null;
        return location.pathname.split('/app/')[1].split('/')[0];
    }, [location.pathname]);

    const activeSubPath = useMemo(() => {
        if (!activeAppId) return '';
        const prefix = `/app/${activeAppId}/`;
        return location.pathname.startsWith(prefix)
            ? location.pathname.slice(prefix.length)
            : '';
    }, [location.pathname, activeAppId]);

    const visibleMFEs = useMemo(() => {
        if (!activeAppId) return { bottom: [], overlay: [] };

        const visible = persistentMFEs.filter(config =>
            hasFeature(config.requiredPermission) &&
            config.visibleWith?.includes(activeAppId) &&
            !config.excludeSubPaths?.[activeAppId]?.includes(activeSubPath)
        );

        return {
            bottom: visible.filter(c => c.panelMode === 'bottom'),
            overlay: visible.filter(c => c.panelMode === 'overlay'),
        };
    }, [persistentMFEs, activeAppId, activeSubPath, hasFeature]);

    return { visibleMFEs, activeAppId, activeSubPath };
};

/**
 * Renders a single persistent MFE.
 */
const PersistentMFE = ({ config, activeAppId, activeSubPath }) => {
    const style = PANEL_STYLES[config.panelMode];

    const loader = (
        <MicroFrontendLoader
            appId={config.id}
            subPath={`${activeAppId}/${activeSubPath}`}
            mode={config.panelMode}
        />
    );

    return style ? <div style={style}>{loader}</div> : loader;
};

/**
 * Bottom panels — renders inside the document flow (e.g., Reports).
 * Place inside the flex column in Layout.
 */
const Bottom = () => {
    const { visibleMFEs, activeAppId, activeSubPath } = usePersistentMFEs();

    if (visibleMFEs.bottom.length === 0) return null;

    logger.info('Rendering bottom panels', { mfes: visibleMFEs.bottom.map(c => c.id) });

    return (
        <>
            {visibleMFEs.bottom.map(config => (
                <PersistentMFE
                    key={config.id}
                    config={config}
                    activeAppId={activeAppId}
                    activeSubPath={activeSubPath}
                />
            ))}
        </>
    );
};

/**
 * Overlay panels — renders with fixed positioning (e.g., ShopFloor AI).
 * Place outside the flex column in Layout.
 */
const Overlay = () => {
    const { visibleMFEs, activeAppId, activeSubPath } = usePersistentMFEs();

    if (visibleMFEs.overlay.length === 0) return null;

    logger.info('Rendering overlay panels', { mfes: visibleMFEs.overlay.map(c => c.id) });

    return (
        <>
            {visibleMFEs.overlay.map(config => (
                <PersistentMFE
                    key={config.id}
                    config={config}
                    activeAppId={activeAppId}
                    activeSubPath={activeSubPath}
                />
            ))}
        </>
    );
};

const PersistentMFELoader = { Bottom, Overlay };

export default PersistentMFELoader;